import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, BookOpen, Mail } from "lucide-react";
import { toast } from "sonner";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Shows the priced lines QuickBooks will receive before anything is created. */
export function QboPreviewDialog({ jobIds, open, onClose, onCreated }) {
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setPlan(null); setErr(null);
    api.post("/qbo/invoices/preview", { job_ids: jobIds }).then((r) => setPlan(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Couldn't price these jobs"));
  }, [open, jobIds]);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/qbo/invoices", { job_ids: jobIds });
      toast.success(`${data.invoices.length} QuickBooks invoice${data.invoices.length === 1 ? "" : "s"} created: ${data.invoices.map((i) => `#${i.doc_number} ${money(i.total)}${i.emailed_to ? ` → ${i.emailed_to}` : ""}`).join(", ")}`, { duration: 9000 });
      onCreated(data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "QuickBooks push failed", { duration: 8000 }); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="qbo-preview-dialog">
        <DialogHeader><DialogTitle>Review before sending to QuickBooks</DialogTitle></DialogHeader>
        {err && <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="qbo-preview-error">{err}</div>}
        {!plan && !err && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Pricing from rate cards…</div>}
        {plan && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="text-xs text-slate-600">Nothing is created until you click <b>Create</b>. Wrong fee? Close this, fix it on the job or rate card, and try again.{plan.next_number && <> Next invoice number: <b className="font-mono">{plan.next_number}</b>{plan.invoices.length > 1 ? " onward" : ""}.</>}</div>
            {plan.invoices.map((inv, i) => (
              <div key={i} className="rounded-md border border-slate-200 overflow-hidden" data-testid={`qbo-preview-invoice-${i}`}>
                <div className="flex items-center justify-between gap-2 bg-[#0b1f3a] text-white px-3 py-2 text-sm"><div><span className="font-semibold">{inv.customer}</span>{inv.grouped && <span className="ml-2 rounded bg-sky-200/30 px-1.5 text-[10px] uppercase tracking-wide">grouped · {inv.jobs.length} jobs</span>}</div><div className="font-semibold tabular-nums" data-testid={`qbo-preview-total-${i}`}>{money(inv.total)}</div></div>
                <table className="w-full text-xs">
                  <tbody>{inv.lines.map((l, k) => (
                    <tr key={k} className="border-t border-slate-100"><td className="px-3 py-1.5 text-slate-500 w-28 font-mono">{l.job_number}</td><td className="px-3 py-1.5 text-slate-800">{l.label}</td><td className="px-3 py-1.5 text-slate-400 w-40 truncate" title="QuickBooks product/service">{l.item || "—"}</td><td className="px-3 py-1.5 text-right tabular-nums font-medium w-24">{money(l.amount)}</td></tr>
                  ))}</tbody>
                </table>
                {inv.email_to && <div className="px-3 py-1.5 border-t border-slate-100 text-[11px] text-emerald-800 inline-flex items-center gap-1"><Mail className="h-3 w-3" />QuickBooks will email this to {inv.email_to}</div>}
              </div>
            ))}
            {plan.invoices.length > 1 && <div className="text-right text-sm font-semibold" data-testid="qbo-preview-grand">Total {money(plan.total)}</div>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="qbo-preview-cancel">Cancel</Button>
          <Button className="bg-[#2ca01c] hover:bg-[#248a17] text-white" onClick={create} disabled={!plan || busy} data-testid="qbo-preview-create">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><BookOpen className="h-4 w-4 mr-1.5" />Create {plan ? `${plan.invoices.length} invoice${plan.invoices.length === 1 ? "" : "s"}` : ""}</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
